// Copyright (c) Docugami, Inc. All rights reserved.

import { SelectionModeProviderResponse } from "messaging/messages/SelectionModeMessages";
import { MessageId } from "messaging/MessageTypes";
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
import { disableModalSelection, enableModalSelection, setModalDisplayContext } from "store/documents/actions";
import { ModalSelectionArea, ModalSelectionDisplayContext, ModalSelectionItemType } from "store/documents/types";
import { RequestState, useAsyncHelper } from "./AsyncHelper";
import { useMessageHandler } from "./MessageHandler";
import { useMessagePublisher } from "./MessagePublisher";
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { Telemetry } from "telemetry/TelemetryManager";
import { SelectionModeChangedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { useAppDispatch, useAppSelector } from 'hooks/redux';

class ModalAreaRegistrar {
    private _areas: ModalAreaDefinition[] = [];
    get areas(): ModalAreaDefinition[] { return this._areas; }

    addArea(name: ModalSelectionArea, ref: MutableRefObject<HTMLDivElement | null>): void {
        this._areas.push({
            areaName: name,
            areaRef: ref
        });
    }

    removeArea(name: ModalSelectionArea): void {
        this._areas = this._areas.filter(x => x.areaName !== name);
    }
}

const ModalAreaRegistrarInstance: ModalAreaRegistrar = new ModalAreaRegistrar();

export interface ModalAreaDefinition {
    areaName: ModalSelectionArea;
    areaRef: MutableRefObject<HTMLDivElement | null>;
}

/**
 * Global hook for managing and checking active modal selection areas
 */
export function useModalSelectionAreas(documentId: string) {
    const [isTracking, setIsTracking] = useState(false);
    const isActive = useSelector((state: AppState) => state.documents.modalSelectionContext.isActive);
    const activeAreas = useSelector((state: AppState) => state.documents.modalSelectionContext.areas)
    const dispatch = useAppDispatch();

    useEffect(() => {
        return () => { dispatch(disableModalSelection()) }
    }, [dispatch]);

    useEffect(() => {
        dispatch(disableModalSelection());
    }, [documentId, dispatch]);

    useEffect(() => {
        if (!activeAreas?.length) {
            return;
        }

        function onClick(ev: MouseEvent) {
            if (!isActive || !isTracking) {
                setIsTracking(false);
                return;
            }

            if (checkAreas(ev)) {
                dispatch(disableModalSelection());
            }
        }

        function onMouseDown(ev: MouseEvent) {
            if (!isActive) {
                return;
            }

            setIsTracking(checkAreas(ev));
        }

        function isOutsideArea(ev: MouseEvent, area: HTMLDivElement | null) {
            if (!area) {
                return false;
            }

            const bounds = area.getBoundingClientRect();
            return ev.clientY > bounds.bottom || ev.clientY < bounds.top || ev.clientX < bounds.left || ev.clientX > bounds.right;
        }

        function checkAreas(ev: MouseEvent): boolean {
            if (!activeAreas) {
                return false;
            }

            const targetAreas = ModalAreaRegistrarInstance.areas.filter(a => activeAreas.includes(a.areaName));
            for (const area of targetAreas) {
                if (!isOutsideArea(ev, area.areaRef.current)) {
                    return false;
                }
            }

            return true;
        }

        document.addEventListener('mousedown', onMouseDown, true);
        document.addEventListener('click', onClick, true);

        return () => {
            document.removeEventListener('mousedown', onMouseDown, true);
            document.removeEventListener('click', onClick, true);
        };
    }, [isActive, isTracking, activeAreas, dispatch]);
}

/**
 * Hook for defining a specific region to apply the modal selection logic to. Any click outside of this region
 * will disable the modal selection.
 */
export function useModalSelectionAreaDefinition(name: ModalSelectionArea, ref: MutableRefObject<HTMLDivElement | null>): void {
    useEffect(() => {
        ModalAreaRegistrarInstance.addArea(name, ref);
        return () => ModalAreaRegistrarInstance.removeArea(name);
    }, [name, ref]);
}

export interface ModalSelectionError {
    title: string;
    message: string;
}

export interface IModalSelection {
    isActive: boolean;
    isActiveExternal: boolean;
    isItemReady: boolean;
    start: (itemType: ModalSelectionItemType, displayContext: ModalSelectionDisplayContext, autoConfirm?: boolean, areas?: ModalSelectionArea[], dropTarget?: boolean) => void;
    stop: () => void;
    errorModal: JSX.Element;
}

/**
 * Hook for interacting with the modal selection control
 * @param onConfirm Callback for if the selection is confirmed for this context
 * @param onCancel Callback for if the selection is cancelled for this context
 */
export function useModalSelection<T>(onConfirm?: (item?: T) => ModalSelectionError | void, onCancel?: () => void): IModalSelection {
    const [isLocal, setIsLocal] = useState(false);
    const [error, setError] = useState<ModalSelectionError | null>(null);

    const isActive = useSelector((state: AppState) => state.documents.modalSelectionContext.isActive);
    const targetItemType = useSelector((state: AppState) => state.documents.modalSelectionContext.itemType ?? ModalSelectionItemType.None);
    const isItemReady = useSelector((state: AppState) => !!state.documents.modalSelectionContext.displayContext?.selectionReady);

    const confirmRef = useRef(onConfirm);
    const cancelRef = useRef(onCancel);

    const dispatch = useAppDispatch();

    const start = useCallback((itemType: ModalSelectionItemType, displayContext: ModalSelectionDisplayContext, autoConfirm?: boolean, areas?: ModalSelectionArea[], dropTarget?: boolean) => {
        if (isActive) {
            return;
        }

        setIsLocal(true);
        dispatch(enableModalSelection(itemType, displayContext, autoConfirm, areas, dropTarget));
    }, [isActive, dispatch]);

    const stop = useCallback(() => {
        if (isLocal) {
            dispatch(disableModalSelection());
            setIsLocal(false)
        }
    }, [isLocal, dispatch]);

    const handleConfirm = useCallback((message: SelectionModeProviderResponse) => {
        if (isLocal) {
            if (targetItemType === message.itemType && !!confirmRef.current) {
                const error = confirmRef.current(message.item);
                if (!!error) {
                    setError(error);
                }
            }

            stop();
        }
    }, [isLocal, targetItemType, stop]);

    const handleCancel = useCallback(() => {
        if (isLocal) {
            if (!!cancelRef.current) {
                cancelRef.current();
            }

            stop();
        }
    }, [isLocal, stop]);

    useMessageHandler(MessageId.SelectionModeProviderResponse, handleConfirm)
    useMessageHandler(MessageId.SelectionModeActionCancel, handleCancel)

    useEffect(() => {
        return () => stop();
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        []);

    useEffect(() => {
        confirmRef.current = onConfirm;
    }, [onConfirm]);

    useEffect(() => {
        cancelRef.current = onCancel;
    }, [onCancel]);

    useEffect(() => {
        if (!isActive && isLocal) {
            handleCancel();
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [isActive]);

    return {
        isActive: isLocal,
        isActiveExternal: isActive && !isLocal,
        isItemReady: isLocal && isItemReady,
        start: start,
        stop: stop,
        errorModal: <ErrorModal error={error} onDismiss={() => setError(null)} />
    }
}

interface ErrorModalProps {
    error: ModalSelectionError | null;
    onDismiss: () => void;
}

const ErrorModal = (props: ErrorModalProps): JSX.Element => {
    return (
        <ConfirmationDialog
            hidden={!props.error}
            confirmedCallback={props.onDismiss}
            dismissedCallback={props.onDismiss}
            confirmButtonText={"OK"}
            title={props.error?.title ?? ''}
            subText={props.error?.message ?? ''} />
    )
}

export interface IModalSelectionProvider {
    isActive: boolean;
    itemType: ModalSelectionItemType;
    setItemReady: (ready: boolean) => void;
}

/**
 * Hook used to declare a modal selection provider. Providers are responsible for providing a specific item type
 * to the modal requestor however they see fit.
 */
export function useModalSelectionProvider<T>(itemTypes: ModalSelectionItemType[], provider: () => Promise<T | undefined>): IModalSelectionProvider {
    const currentType = useAppSelector((state: AppState) => state.documents.modalSelectionContext?.itemType ?? ModalSelectionItemType.None);

    useMessageHandler(MessageId.SelectionModeActionConfirm, handleRequest);
    const { execute: executeProvider, data, state } = useAsyncHelper("Selection provider");

    const { sendMessage } = useMessagePublisher();
    const dispatch = useAppDispatch();

    function handleRequest() {
        if (itemTypes.includes(currentType)) {
            executeProvider(provider());
        }
    }

    useEffect(() => {
        if (state === RequestState.Error || state === RequestState.Complete) {
            const message: SelectionModeProviderResponse = {
                id: MessageId.SelectionModeProviderResponse,
                itemType: currentType,
                item: data
            }

            sendMessage(message);
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [state]);

    const setItemReady = useCallback((ready: boolean) => {
        dispatch(setModalDisplayContext({ selectionReady: ready }));
    }, [dispatch]);

    const isActive = useMemo(() => itemTypes.includes(currentType), [currentType, itemTypes]);

    return {
        isActive: isActive,
        itemType: currentType,
        setItemReady: setItemReady
    }
}

interface ModalSelectionListener {
    isActive: boolean;
}

export const useModalSelectionListener = (types: ModalSelectionItemType[], onConfirm?: (item?: any) => ModalSelectionError | void, onCancel?: () => void): ModalSelectionListener => {
    const isActive = useSelector((state: AppState) => state.documents.modalSelectionContext.isActive);
    const targetItemType = useSelector((state: AppState) => state.documents.modalSelectionContext.itemType ?? ModalSelectionItemType.None);

    const confirmRef = useRef(onConfirm);
    const cancelRef = useRef(onCancel);

    const handleConfirm = useCallback((message: SelectionModeProviderResponse) => {
        if (types.includes(message.itemType)) {
            confirmRef.current?.(message.item);
        }
    }, [types]);

    const handleCancel = useCallback(() => {
        if (types.includes(targetItemType)) {
            cancelRef.current?.();
        }
    }, [types, targetItemType]);

    useMessageHandler(MessageId.SelectionModeProviderResponse, handleConfirm)
    useMessageHandler(MessageId.SelectionModeActionCancel, handleCancel)

    useEffect(() => {
        confirmRef.current = onConfirm;
    }, [onConfirm]);

    useEffect(() => {
        cancelRef.current = onCancel;
    }, [onCancel]);

    return {
        isActive: isActive && types.includes(targetItemType)
    }
}

interface ModalSelectionActions {
    confirm: () => void;
    cancel: () => void;
}

export const useModalSelectionActions = (): ModalSelectionActions => {
    const isActive = useSelector((state: AppState) => state.documents.modalSelectionContext.isActive);
    const isSelectionReady = useSelector((state: AppState) => !!state.documents.modalSelectionContext.displayContext?.selectionReady);

    const { sendMessage } = useMessagePublisher();

    const confirm = useCallback(() => {
        if (isActive && isSelectionReady) {
            sendMessage({ id: MessageId.SelectionModeActionConfirm });
            Telemetry.trackEvent<SelectionModeChangedSchema>({
                isActive: true,
                eventName: TelemetryEvent.SelectionModeChanged
            });
        }
    }, [isActive, isSelectionReady, sendMessage]);

    const cancel = useCallback(() => {
        if (isActive) {
            sendMessage({ id: MessageId.SelectionModeActionCancel });
            Telemetry.trackEvent<SelectionModeChangedSchema>({
                isActive: false,
                eventName: TelemetryEvent.SelectionModeChanged
            });
        }
    }, [isActive, sendMessage])

    return {
        confirm: confirm,
        cancel: cancel
    }
}
